import { useLayoutEffect, useState } from 'react';

// The picked-area chips under the search (kartvy's SearchBox and listvy's search band) show at most two
// rows. The chips that don't fit are hidden behind a "+N" chip at the end of the second row, which shows
// them all (and then reads "Visa färre").
//
// The container marks each chip with `data-chip` and the toggle with `data-chip-toggle`; anything after the
// toggle (kartvy's Rensa) has to fit on the second row too. Chips past the returned count get `hidden`.
// Measuring toggles `hidden` on the DOM for a moment and puts it back before React renders the result.
const MAX_ROWS = 2;

const measureVisible = (container) => {
    const chips = [...container.querySelectorAll('[data-chip]')];
    const toggle = container.querySelector('[data-chip-toggle]');
    if (!chips.length) return 0;
    const saved = chips.map((chip) => chip.hidden);
    const savedToggle = toggle ? toggle.hidden : null;
    chips.forEach((chip) => { chip.hidden = false; });
    if (toggle) toggle.hidden = true;

    const first = chips[0];
    const gap = parseFloat(getComputedStyle(container).rowGap) || 0;
    const rowStep = first.offsetHeight + gap;
    // Anything whose top is above this sits in the first two rows.
    const limit = first.offsetTop + rowStep * (MAX_ROWS - 0.5);
    // The last element on show (a hidden one reports a top of 0).
    const last = () => [...container.children].reverse().find((child) => !child.hidden);
    const fits = () => (last()?.offsetTop ?? 0) < limit;

    let visible = chips.length;
    if (!fits()) {
        if (toggle) toggle.hidden = false;
        while (visible > 0 && !fits()) {
            visible -= 1;
            chips[visible].hidden = true;
        }
    }

    chips.forEach((chip, index) => { chip.hidden = saved[index]; });
    if (toggle) toggle.hidden = savedToggle;
    return visible;
};

// Returns { ref (for the container), visibleCount, hiddenCount, expanded, setExpanded, hasMore }. `signature`
// changes when the chips' text does (their widths change).
export default function useTwoChipRows(count, signature = '') {
    // A callback ref: kartvy's chips unmount while the search list is open and need measuring when they return.
    const [container, ref] = useState(null);
    const [collapsedCount, setCollapsedCount] = useState(count);
    const [expanded, setExpanded] = useState(false);

    useLayoutEffect(() => {
        if (!container) return undefined;
        const update = () => setCollapsedCount(measureVisible(container));
        update();
        let width = container.offsetWidth;
        const observer = typeof ResizeObserver === 'function'
            ? new ResizeObserver(() => {
                if (container.offsetWidth === width) return;
                width = container.offsetWidth;
                update();
            })
            : null;
        observer?.observe(container);
        return () => observer?.disconnect();
    }, [container, count, signature, expanded]);

    const hasMore = collapsedCount < count;
    // Nothing left to fold away: back to the folded state for the next time chips pile up.
    useLayoutEffect(() => {
        if (!hasMore && expanded) setExpanded(false);
    }, [hasMore, expanded]);

    const visibleCount = expanded ? count : Math.min(collapsedCount, count);
    return { ref, visibleCount, hiddenCount: count - visibleCount, expanded, setExpanded, hasMore };
}
